import React, { useEffect, useState } from "react";
import { Alert, Button, Container, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent, TextField } from "@mui/material";
import { NavLink } from "react-router-dom";

import { IVendorExportRequest, IVendorLight } from "../../../types/vendor";

import { AppConfig } from "../../../config/appConfig";
import { useUserContext } from "../../../contexts/userContext";

import { VendorService } from "../../../services/vendorService";
import { UtilityService } from "../../../services/utilityService";

import Navigation from "../../header";
import PermissionGate from "../../permissionGate";
import Spinner from "../../spinner";
import VendorTable from "../../tables/vendorTable";
import WorkingModal, { IWorkingModalProps } from "../../workingModal";

import "../../../styles/entity/vendorList.scss";

const VendorList = () => {
	const userContext = useUserContext();
	const [gridView, setGridView] = useState<IVendorLight[]>();

	//Filtering
	const [stateOptions, setStateOptions] = useState<string[]>([]);
	const [cityOptions, setCityOptions] = useState<string[]>([]);
	const [statusOptions, setStatusOptions] = useState<string[]>([]);
	const [nameFilter, setNameFilter] = useState("");
	const [stateFilter, setStateFilter] = useState("All");
	const [cityFilter, setCityFilter] = useState("All");
	const [statusFilter, setStatusFilter] = useState("Active");
	const [filteredResultCount, setFilteredResultCount] = useState(0);

	const [isLoadingVendors, setisLoadingVendors] = useState(false);
	const [errorMessage, setErrorMessage] = useState<string | undefined>();
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);
	const [exportSortField, setExportSortField] = useState("");
	const [exportSortAscending, setExportSortAscending] = useState(true);

	const loadVendorData = async () => {
		setisLoadingVendors(true);
		setErrorMessage(undefined);

		VendorService.getVendors()
			.then((response) => {
				console.log('VendorService.getVendors: SUCCESS', response);
				let cities: string[] = [];
				let states: string[] = [];
				let statuses: string[] = [];

				let vendors = VendorService.mapVendorLightList(response.data);

				vendors.map((item) => {
					if (item.city && !cities.includes(item.city)) {
						cities.push(item.city);
					}

					if (item.state && !states.includes(item.state)) {
						states.push(item.state);
					}

					if (item.status && !statuses.includes(item.status)) {
						statuses.push(item.status);
					}
				});

				setGridView(vendors);
				setCityOptions(cities.sort());
				setStateOptions(states.sort());
				setStatusOptions(statuses.sort());
			})
			.catch((error) => {
				console.log('VendorService.getVendors: ERROR', error);
				if (error?.code == "ERR_NETWORK") {
					setErrorMessage(AppConfig.apiConnectionErrorMessage)
				}
			})
			.finally(() => {
				setisLoadingVendors(false);
			});
	}

	useEffect(() => {
		loadVendorData();
	}, [])

	const handleSelectState = (event: SelectChangeEvent) => {
		setStateFilter(event.target.value);
	};

	const handleSelectCity = (event: SelectChangeEvent) => {
		setCityFilter(event.target.value);
	};

	const handleSelectStatus = (event: SelectChangeEvent) => {
		setStatusFilter(event.target.value);
	};

	const handleFilteredResultsChanged = (results: IVendorLight[]) => {
		setFilteredResultCount(results?.length ?? 0);
	};

	const handleSortOptionsChanged = (sortField: string, sortAscending: boolean) => {
		setExportSortField(sortField);
		setExportSortAscending(sortAscending);
	};

	const handleExportVendors = () => {
		setWorkingModalParams({ params: { content: "Exporting vendors..." } });

		var request: IVendorExportRequest = {
			name: nameFilter?.trim() ? nameFilter?.trim() : undefined,
			state: stateFilter == "All" ? undefined : stateFilter,
			city: cityFilter == "All" ? undefined : cityFilter,
			status: statusFilter == "All" ? undefined : statusFilter.toLowerCase() == "active",
			sortField: exportSortField,
			sortAscending: exportSortAscending
		};

		VendorService.exportVendorListToExcel(request)
			.then((response) => {
				console.log('VendorService.exportVendorListToExcel: SUCCESS', response);
				UtilityService.downloadGeneratedDocument(response.data);
			})
			.catch((error) => {
				console.log('VendorService.exportVendorListToExcel: ERROR', error);
			})
			.finally(() => {
				setWorkingModalParams(null);
			});
	};

	return (
		<div className="hwi-vendor-list">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canManageVendors}>
					<section className="hwi-entity-search-bar">
						<div className="search-header">
							<div className="title">Search Vendors</div>
							<div className="actions">
								{userContext.permissions?.canExportVendors && <Button variant="contained" color="primary" disabled={isLoadingVendors || !filteredResultCount} onClick={handleExportVendors}>Export Vendors</Button>}
								<NavLink to="/vendor/add">
									<Button variant="outlined">New Vendor</Button>
								</NavLink>
							</div>
						</div>
						<section className="search-options">
							<FormControl size="small">
								<TextField
									label="Name"
									name="name"
									value={nameFilter}
									size="small"
									autoComplete="off"
									onChange={(event) => { setNameFilter(event.target.value) }}
								></TextField>
							</FormControl>
							<FormControl size="small">
								<InputLabel>State</InputLabel>
								<Select
									value={stateFilter}
									label="State"
									onChange={handleSelectState}
								>
									<MenuItem value="All" key="-1">All</MenuItem>
									{stateOptions.map((item, index) => (
										<MenuItem value={item} key={index}>{item}</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small">
								<InputLabel>City</InputLabel>
								<Select
									value={cityFilter}
									label="City"
									onChange={handleSelectCity}
								>
									<MenuItem value="All" key="-1">All</MenuItem>
									{cityOptions.map((item, index) => (
										<MenuItem value={item} key={index}>{item}</MenuItem>
									))}
								</Select>
							</FormControl>
							<FormControl size="small">
								<InputLabel>Status</InputLabel>
								<Select
									value={statusFilter}
									label="Status"
									onChange={handleSelectStatus}
								>
									<MenuItem value="All" key="-1">All</MenuItem>
									{statusOptions.map((item, index) => (
										<MenuItem value={item} key={index}>{item}</MenuItem>
									))}
								</Select>
							</FormControl>
						</section>
					</section>
					<Spinner dependency={isLoadingVendors} text="Loading Vendors...">
						<section className="results">
							{errorMessage &&
								<Alert className="alert-error"
									severity="error">
									{errorMessage}
								</Alert>
							}
							{!errorMessage &&
								<VendorTable
									data={gridView}
									name={nameFilter}
									state={stateFilter}
									city={cityFilter}
									status={statusFilter}
									onFilteredResultsChanged={handleFilteredResultsChanged}
									onSortOptionschanged={handleSortOptionsChanged}
								></VendorTable>
							}
						</section>
					</Spinner>
				</PermissionGate>
				<WorkingModal params={workingModalParams?.params}></WorkingModal>
			</Container>
		</div>
	);
};

export default VendorList;